import * as StringUtils from '../../utils/string-utils';
import { timerHelperModule } from '../../helpers/timer-helper';
import * as EventUtils from '../../utils/event-utils';
import * as ObjectUtils from '../../utils/object-utils/object-utils';
import { configServiceModule } from '../config-service/config-service';
import { appConfig } from '../../constants/app-config';

import './intercom-service.less';

// since we split intercom based off of CS & TS, we need to continue to maintain our own integration
export const intercomServiceModule = angular
  .module('Q.services.intercom-service', [
    timerHelperModule,
    configServiceModule,
  ])
  .factory(
    'intercomService',
    function ($window, timerHelper, $rootScope, configService, $log) {
      let userProps;
      let booted = false;
      let showing = false;
      let updateTimer = null;
      let service = {
        setUser(user, attempts = 0) {
          user = user.user || user;

          /*eslint camelcase: 0 */
          userProps = {
            user_id: user.id,
            user_type: user.$type,
            email: user.email,
            name: user.name,
          };

          if (user.team) {
            userProps.company = {
              id: user.team.id,
              name: user.team.name,
              created_at: user.team.createdAt,
              enrollment: user.team.enrollment,
              website: user.team.website,
            };
          }

          function _tryAgain(intercomMissing) {
            if (attempts < 10) {
              attempts = intercomMissing === true ? attempts + 1 : attempts + 5;
              return timerHelper.longTimeout(
                () => service.setUser(user, attempts),
                1000,
                false,
              );
            }
          }

          if ($window.Intercom) {
            configService
              .load(attempts > 0)
              .then((cfg) => {
                if (!userProps || user.$type === 'Candidate') return;
                userProps.app_id = cfg.intercomAppId.ts;
                if (booted) {
                  service.update(userProps);
                } else {
                  $window.Intercom('boot', userProps);
                  // call update again just to catch any first time user events
                  service.delayUpdate();
                  $window.Intercom('onShow', () => (showing = true));
                  $window.Intercom('onHide', () => (showing = false));
                  booted = true;
                  $rootScope.$broadcast('intercom.isActive', true);
                }
              })
              .catch(_tryAgain);
          } else {
            _tryAgain(true);
          }
        },
        unsetUser() {
          if (userProps || booted) {
            service.call('shutdown');
            showing = booted = false;
            userProps = null;
            $rootScope.$broadcast('intercom.isActive', false);
          }
        },
        trackPage() {
          service.call('update');
        },
        trackEvent(name, properties) {
          // only track if the user is set and intercom is loaded
          if (userProps && $window.Intercom) {
            // intercom does a crappy job of formatting events so split camel case values before sending
            service.call(
              'trackEvent',
              StringUtils.snakeCase(name),
              ObjectUtils.flattenObject(properties, true),
            );
            // do a delayed update to check for new messages in response to the sent event
            service.delayUpdate();
          }

          return service;
        },
        delayUpdate(delay, data) {
          timerHelper.longTimeout.cancel(updateTimer);

          updateTimer = timerHelper.longTimeout(
            function () {
              service.update(data);
            },
            delay || 2000,
            false,
          );
        },
        update(data) {
          if (data || userProps) {
            return service.call('update', data || userProps);
          }
          return service;
        },
        // simple wrapper to make sure we are not calling methods on a non-existing Intercom object
        call(action, ...args) {
          if ($window.Intercom) {
            try {
              $window.Intercom(action, ...args);
            } catch (e) {
              if (!service.reportedCallError) {
                $log.error(e);
                service.reportedCallError = true;
              }
            }
          }
          return service;
        },
        show(msg) {
          showing = true;
          if (msg) {
            if (msg === true) msg = '';
            return service.call('showNewMessage', msg);
          } else {
            return service.call('show');
          }
        },
        hide() {
          showing = false;
          return service.call('hide');
        },
        toggle() {
          return showing ? service.hide() : service.show();
        },
        isActive() {
          return booted;
        },
        showWidget() {
          return booted && appConfig.intercomWidget;
        },
      };

      return service;
    },
  )
  .run(function ($rootScope, intercomService) {
    $rootScope.$on('$stateChangeSuccess', () =>
      intercomService.delayUpdate(1000),
    );
  })
  .directive('intercomHide', function (intercomService) {
    return {
      restrict: 'A',
      link(scope, elem) {
        elem.toggleClass('ng-hide', !intercomService.showWidget());
        scope.$on('intercom.isActive', () =>
          elem.toggleClass('ng-hide', !intercomService.showWidget()),
        );
      },
    };
  })
  .directive('intercomButton', function (intercomService) {
    return {
      restrict: 'A',
      link(scope, elem, attr) {
        elem.addClass('intercom-button');
        elem.attr('tabindex', 0);
        elem.on('click', (event) => {
          if (intercomService.showWidget()) {
            EventUtils.stopEvent(event);
            let msg = attr.intercomButtonMessage;
            if (msg === 'true' || msg === '') msg = true;
            intercomService.show(msg);
          }
        });
        if (!attr.title && !attr.tooltip) {
          elem.attr('title', 'Click to chat with the Qualified Team');
        }
        if (!attr.href) {
          elem.toggleClass('ng-hide', !intercomService.showWidget());
          scope.$on('intercom.isActive', () =>
            elem.toggleClass('ng-hide', !intercomService.showWidget()),
          );
        }
      },
    };
  })

  .directive('modalContent', function (intercomService, $compile) {
    return {
      restrict: 'C',
      link(scope, elem) {
        let nodeLink = $compile(
          `<a intercom-button track-click="Intercom Conversations Shown" track-data="{ui: {region: 'dialog'}}"></a>`,
        )(scope);
        elem.append(nodeLink);
      },
    };
  });
